import React, { Suspense, lazy } from "react";
// Layouts and Guards (keep as normal imports)
import MainLayout from "../components/layouts/sidebar";
import AuthGuard from "../utils/auth-guard/auth-gaurd";
import AdvertiserMainLayout from "../components/layouts/adv-sidebar";
import RoleGuard from "../utils/role-guard";
import AdminDashboardLayout from "../components/layouts/admin-sidebar";
import Settings from "../pages/recommender/settings";
import UserInterest from "../pages/auth/verification&interest/interest";
import VerifyBVN from "../pages/auth/verification&interest/verify-bvn";
import LinkYourBank from "../pages/auth/verification&interest/link-bank";
import AdvertiserWallet from "../pages/advertiser/wallet";
import RecommenderPayouts from "../pages/admin/rcommender-payouts";
import WalletSubscription from "../pages/recommender/wallet/wallet-subscription";
import DiscoveryLinks from "../pages/admin/discovery-links";


// ================= Lazy-load ALL pages =================
const Dashboard = lazy(() => import("../pages/recommender/dashboard"));
const MyLinks = lazy(() => import("../pages/recommender/my-links"));
const ReferralDashboard = lazy(() => import("../pages/recommender/referrals"));
const Rewards = lazy(() => import("../pages/recommender/rewards"));
const Wallet = lazy(() => import("../pages/recommender/wallet"));
const ActivityHistory = lazy(() => import("../pages/recommender/activity-history"));

const AdvertiserDashboard = lazy(() => import("../pages/advertiser/dashboard"));
const Reccommenders = lazy(() => import("../pages/advertiser/recommenders"));
const Commissions = lazy(() => import("../pages/advertiser/reward-management"));
const AccountSettings = lazy(() => import("../pages/advertiser/account-settings"));
const Integrations = lazy(() => import("../pages/advertiser/integrations"));


const WaitlistDashboard = lazy(() => import("../pages/waitlist-dashboard"));
const IntermediaryPage = lazy(() => import("../pages/recommender/intermediary"));
const InterstitialScreen = lazy(() => import("../pages/recommender/intermediary/inter-screen"));

const AllBrands = lazy(() => import("../pages/recommender/link/brands"));
const ExploreRewards = lazy(() => import("../pages/recommender/link/explore"));

const AdminDashboard = lazy(() => import("../pages/admin/dashboard"));
const AdminRecommender = lazy(() => import("../pages/admin/recommenders"));
const AdminRecommenderDetail = lazy(() => import("../pages/admin/recommenders/detail"));
const AdminAdvertisers = lazy(() => import("../pages/admin/advertisers"));
const AdminAdvertiserDetail = lazy(() => import("../pages/admin/advertisers/detail"));
const AdminGlobalSettings = lazy(() => import("../pages/admin/global-settings"));

// Loading component
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A86B]"></div>
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

// Wrapper component to add Suspense
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// ========== ROUTING CONFIG ========== //
// Explore Rewards is public, but logged-in users get it behind AuthGuard. The token has to be
// read at render time — reading it once at module load meant that after logging out (no full
// reload) the route still required auth, so "Explore Rewards" bounced back to /login until a refresh.
const ExploreRewardsRoute = () =>
  localStorage.getItem("authToken") ? (
    <AuthGuard>
      <SuspenseWrapper>
        <ExploreRewards />
      </SuspenseWrapper>
    </AuthGuard>
  ) : (
    <SuspenseWrapper>
      <ExploreRewards />
    </SuspenseWrapper>
  );
const AppRoutes = [
  {
    path: "/waitlist-dashboard",
    element: (
      <SuspenseWrapper>
        <WaitlistDashboard />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/inter/:id",
    element: (
      <AuthGuard>
        <SuspenseWrapper>
          <IntermediaryPage />
        </SuspenseWrapper>
      </AuthGuard>
    )
  },
  {
    path: "/inter/page/:code",
    element: (
      <SuspenseWrapper>
        <InterstitialScreen />
      </SuspenseWrapper>
    )
  },
  {
    path: "/brands",
    element: (
      <AuthGuard>
        <SuspenseWrapper>
          <AllBrands />
        </SuspenseWrapper>
      </AuthGuard>
    )
  },
  {
    path: "/:referralCode?",
    element: <ExploreRewardsRoute />,
  },

  {
    path: "/user/interest",
    element: (
      <SuspenseWrapper>
        <UserInterest />
      </SuspenseWrapper>
    )
  },

  {
    path: "/verify/bvn",
    element: (
      <SuspenseWrapper>
        <VerifyBVN />
      </SuspenseWrapper>
    )
  },

  {
    path: "/link/bank",
    element: (
      <SuspenseWrapper>
        <LinkYourBank />
      </SuspenseWrapper>
    )
  },

  {
    path: "/wallet/subscription/:id?",
    element: (
      <SuspenseWrapper>
        <WalletSubscription />
      </SuspenseWrapper>
    )
  },
  // ================= RECOMMENDER =================
  {
    path: "/",
    element: (
      <AuthGuard>
        <RoleGuard role="RECOMMENDER">
          <MainLayout />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "profile",
        element: (
          <SuspenseWrapper>
            <Dashboard />
          </SuspenseWrapper>
        )
      },
      {
        path: "links",
        element: (
          <SuspenseWrapper>
            <MyLinks />
          </SuspenseWrapper>
        )
      },
      {
        path: "profile/activities",
        element: (
          <SuspenseWrapper>
            <ActivityHistory />
          </SuspenseWrapper>
        )
      },
      {
        path: "wallet",
        element: (
          <SuspenseWrapper>
            <Wallet />
          </SuspenseWrapper>
        )
      },
      {
        path: "rewards",
        element: (
          <SuspenseWrapper>
            <Rewards />
          </SuspenseWrapper>
        )
      },
      {
        path: "referrals",
        element: (
          <SuspenseWrapper>
            <ReferralDashboard />
          </SuspenseWrapper>
        )
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <Settings />
          </SuspenseWrapper>
        )
      },

    ],
  },

  // ================= ADVERTISER =================
  {
    path: "/business",
    element: (
      <AuthGuard>
        <RoleGuard role="ADVERTISER">
          <AdvertiserMainLayout />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "profile",
        element: (
          <SuspenseWrapper>
            <AdvertiserDashboard />
          </SuspenseWrapper>
        )
      },
      {
        path: "recommenders",
        element: (
          <SuspenseWrapper>
            <Reccommenders />
          </SuspenseWrapper>
        )
      },
      {
        path: "commissions",
        element: (
          <SuspenseWrapper>
            <Commissions />
          </SuspenseWrapper>
        )
      },
      {
        path: "integrations",
        element: (
          <SuspenseWrapper>
            <Integrations />
          </SuspenseWrapper>
        )
      },
      {
        path: "wallet",
        element: (
          <SuspenseWrapper>
            <AdvertiserWallet />
          </SuspenseWrapper>
        )
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <AccountSettings />
          </SuspenseWrapper>
        )
      },
    ],
  },

  // ================= ADMIN =================
  {
    path: "/admin",
    element: (
      // <AuthGuard>
      //   <RoleGuard role="ADMIN">
      <AdminDashboardLayout />
      //   </RoleGuard>
      // </AuthGuard>
    ),
    children: [
      {
        path: "overview",
        element: (
          <SuspenseWrapper>
            <AdminDashboard />
          </SuspenseWrapper>
        )
      },
      {
        path: "recommender",
        element: (
          <SuspenseWrapper>
            <AdminRecommender />
          </SuspenseWrapper>
        )
      },
      {
        path: "recommender/:id",
        element: (
          <SuspenseWrapper>
            <AdminRecommenderDetail />
          </SuspenseWrapper>
        )
      },
      {
        path: "advertisers",
        element: (
          <SuspenseWrapper>
            <AdminAdvertisers />
          </SuspenseWrapper>
        )
      },
      {
        path: "advertisers/:id",
        element: (
          <SuspenseWrapper>
            <AdminAdvertiserDetail />
          </SuspenseWrapper>
        )
      },
      {
        path: "payouts",
        element: (
          <SuspenseWrapper>
            <RecommenderPayouts />
          </SuspenseWrapper>
        )
      },
      {
        path: "discovery/links",
        element: (
          <SuspenseWrapper>
            <DiscoveryLinks />
          </SuspenseWrapper>
        )
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <AdminGlobalSettings />
          </SuspenseWrapper>
        )
      },
    ],
  },
];

export default AppRoutes;